/**
 * BomSection — Resources section body.
 *
 * Section label reads "Resources" in the Progress tab (renamed from
 * "Expenditures" / "Budget" per POLICY_RESOURCE_VOCABULARY.md).
 * Underlying data model still uses "BOM item" (bomItemGuid,
 * MiniProjectBomItem) — rename tracked separately.
 *
 * Uses MiniProjectBomItemCard for visual consistency with Mission
 * (MiniProjectGoalCard) and Schedule (MiniProjectTaskCard). The pencil
 * on each card opens MiniProjectBomItemEditTray (DEV-1110 Phase 2,
 * replaced the inline EditBomForm + AddBomForm). Procurement-state
 * quick-cycle still lives on the card itself.
 *
 * Template items can be marked not_applicable but not deleted (per
 * migration 170: soft-remove via N/A state).
 *
 * Strings localized via `pages:miniProject.sections.*` (shared form
 * vocabulary) and `pages:miniProject.sections.bom.*` (BOM-specific).
 */

import { useState } from "react";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { type MiniProjectBomItem } from "../../../api/miniProjects";
import MiniProjectBomItemCard from "../../../Components/MiniProjectBomItemCard/MiniProjectBomItemCard";
import MiniProjectBomItemEditTray from "../../../Components/MiniProjectBomItemEdit/MiniProjectBomItemEditTray";
import styles from "./sections.module.css";

interface Props {
  miniProjectGuid: string;
  bomItems: MiniProjectBomItem[];
  isLead: boolean;
  onMutated: () => void;
  /** Add-tray open state, lifted to ProgressTab so the SectionPanel
   *  header "+" button can trigger it (DEV-1081 Chunk D). */
  addOpen?: boolean;
  onCloseAdd?: () => void;
}

export default function BomSection({
  miniProjectGuid,
  bomItems,
  isLead,
  onMutated,
  addOpen = false,
  onCloseAdd,
}: Props) {
  const { t } = useAppTranslation("pages");
  // Edit-tray target stored as guid (not the full item object) so that
  // when the in-tray procurement-state pill cycles and triggers a
  // parent refetch, the tray sees the *latest* item from the updated
  // bomItems[] — not a stale snapshot captured at pencil-click.
  const [editingItemGuid, setEditingItemGuid] = useState<string | null>(null);
  const editingItem = editingItemGuid
    ? bomItems.find((item) => item.bomItemGuid === editingItemGuid) ?? null
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {bomItems.length === 0 && !addOpen ? (
        <p className={styles.rowMeta}>{t("miniProject.sections.bom.empty")}</p>
      ) : (
        bomItems.map((item) => (
          <MiniProjectBomItemCard
            key={item.bomItemGuid}
            miniProjectGuid={miniProjectGuid}
            item={item}
            isLead={isLead}
            onMutated={onMutated}
            onEdit={() => setEditingItemGuid(item.bomItemGuid)}
          />
        ))
      )}

      {/* Two tray instances — only one is open at a time. Keying on
          the active record/state makes useState initializers fire fresh
          on each open so the form populates from the right source. */}
      <MiniProjectBomItemEditTray
        key={`bom-create-${addOpen ? "open" : "closed"}`}
        mode="create"
        open={addOpen}
        miniProjectGuid={miniProjectGuid}
        onClose={() => onCloseAdd?.()}
        onMutated={() => {
          onMutated();
          onCloseAdd?.();
        }}
      />
      <MiniProjectBomItemEditTray
        key={`bom-edit-${editingItemGuid ?? "none"}`}
        mode="edit"
        open={editingItem !== null}
        item={editingItem}
        miniProjectGuid={miniProjectGuid}
        onClose={() => setEditingItemGuid(null)}
        onMutated={() => {
          // Do NOT clear editingItemGuid here — the in-tray state pill
          // fires onMutated() after a cycle, and clearing would collapse
          // the tray every time. Close paths (Cancel / Esc / scrim /
          // Save / Delete) close it explicitly.
          onMutated();
        }}
      />
    </div>
  );
}
